import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { getErrorResponse, getTimeRelativeTime, getUpdatedSearchParams, parseSearchParams } from '../../../../services/util'
import { usePopup } from '../../../../context/popupContext'
import { useDeleteRole, usePermissionSubjects, useRolesData, useUpdateRole } from '../../../../hooks/role'
import { DefaultListApiResponse } from '../../../../interfaces/api-response'
import { ListApiFilterParams } from '../../../../interfaces/api-request'
import { CustomTable } from '../../../../components/customTable'
import { RenderDefaultActions, RenderStatus } from '../../common'
import { FilterOptions } from '../../../../components/customTable/custom-table-interfaces'
import moment from 'moment'
import { AddUpdateRole } from './AddUpdateRoleDialog'
import { useNotificationBar } from '../../../../context/notificationContext'
import { SmoothTransition } from '../../../../components/pageTransition'
import { DialogWrapper } from '../../../../components/dialogWrapper'
import { Unauthorized } from '../../../Unauthorized'
import { PagePreloader } from '../../../../components/pagePreloader'

export const Roles = () => {
    let [searchParams, setSearchParams] = useSearchParams()
    let popup = usePopup()
    let notification = useNotificationBar()
    const { data: permissionsResponse } = usePermissionSubjects()
    const permissions = permissionsResponse?.data?.results ?? []

    let filterParams: ListApiFilterParams = parseSearchParams(searchParams, 'filter')
    const { mutate: deleteRole } = useDeleteRole()
    const { mutate: updateRole } = useUpdateRole()

    let { data, isFetching: isLoading, isError, refetch, dataUpdatedAt, isSuccess, error } = useRolesData(filterParams)
    let [addUpdateInput, setaddUpdateInput] = useState<any>({})
    let [addUpdate, setAddUpdate] = useState<any>(false)

    let useFormHook = useForm({ defaultValues: filterParams })

    let responseData: DefaultListApiResponse = data?.data;

    const onStatusChange = async (row: any) => {
        let update: any = { is_active: row.is_active ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.is_active ? 'activate' : 'deactivate'} role ${row.role_name} ?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updateRole({
                        data: update,
                        role_id: row?.id
                    }, {
                        onError(error: any) {
                            notification.push(getErrorResponse(error))
                        },
                        onSuccess(data, variables, context) {
                            notification.push({
                                title: `Role ${update.is_active ? 'Activated' : 'Deactivated'}`,
                                message: `Role "${row.role_name}" has been ${update.is_active ? 'activated' : 'deactivated'} successfully !`,
                                type: update.is_active ? 'success' : 'info'
                            })
                            refetch();
                        },
                    })
                }
            }
        })
    }

    const openAddUpdateDialog = (input: any) => {
        setaddUpdateInput(input)
        setAddUpdate(true)
    }

    const onAction = (actionType: any, row: any) => {
        if (actionType === 'edit') {
            openAddUpdateDialog(row)
        }
        if (actionType === 'delete') {
            popup.open({
                title: 'Are you sure ?',
                message: 'Do you want to delete this role ?',
                type: 'confirm',
                onAction(value) {
                    if (value) {
                        deleteRole({
                            role_id: row.id
                        }, {
                            onSuccess: () => refetch(),
                            onError: (error) => { notification.push(getErrorResponse(error)) }
                        })
                    }
                },
            })
        }
    }
    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'role_name',
            footer: (info) => info.column.id,
            header: () => "ROLE NAME"
        },
        {
            accessorFn: (row) => moment(row.created_date).format('LL'),
            accessorKey: 'created_date',
            header: () => 'CREATED ON',
            footer: (info) => info.column.id,
        },
        {
            id: 'is_active',
            header: 'STATUS',
            accessorKey: 'is_active',
            cell: ({ row }) => RenderStatus(row, 'is_active', onStatusChange),
        },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => RenderDefaultActions(row, ['edit', 'delete'], onAction),
        },

    ]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'role_name'
        }
    }

    const rowSelectionState = useState<RowSelectionState>({})
    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                }
                else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const filterOptions: FilterOptions[] = [{
        key: 'is_active',
        options: [
            {
                display: `All`,
                value: ''
            },
            {
                display: `Active`,
                value: 1
            },
            {
                display: `Inactive`,
                value: 0
            }
        ],
        label: 'Status'
    }

    ]

    const onDataAddUpdate = (values: any) => {
        setAddUpdate(false);
        setaddUpdateInput({});
        refetch();
    }

    return (
        (isSuccess || isError) ?
            <SmoothTransition>
                {
                    (error as any)?.response?.data?.statusCode === 403 ?
                        <Unauthorized /> :
                        <div className='w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
                            <DialogWrapper panelClass="max-w-5xl" title={addUpdateInput?.id ? 'Update Role' : 'Add Role'} isOpen={!!addUpdate} setIsOpen={setAddUpdate}>
                                <AddUpdateRole permissionSubjects={permissions} onSubmitHandler={onDataAddUpdate} input_data={addUpdateInput} setIsOpen={setAddUpdate} />
                            </DialogWrapper>
                            <aside className="flex flex-auto items-center">
                                <CustomTable
                                    title='Role Management'
                                    titleLabel={(!isError) ? `Last fetched on ${getTimeRelativeTime(dataUpdatedAt)}` : ''}
                                    formHook={useFormHook}
                                    columns={columns}
                                    data={responseData?.results}
                                    pages={responseData?.pages}
                                    totalRecords={responseData?.total}
                                    onRefresh={refetch}
                                    isLoading={isLoading}
                                    isErrored={isError}
                                    rowSelectionState={rowSelectionState}
                                    mobileViewOptions={mobileViewOptions}
                                    useFieldForSelect='id'
                                    filterOptions={filterOptions}
                                    addButtonText={'Create Role'}
                                    onAddAction={() => {
                                        setaddUpdateInput({});
                                        setAddUpdate(true)
                                    }}
                                />
                            </aside>
                        </div>
                }
            </SmoothTransition> : <PagePreloader/>
    )
}
